<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import * as LucideIcons from '@lucide/vue';
import { Eye, RefreshCw } from '@lucide/vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog/index';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar/index';
import { Badge } from '@/components/ui/badge/index';
import { Button } from '@/components/ui/button/index';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card/index';
import { Separator } from '@/components/ui/separator/index';
import { Skeleton } from '@/components/ui/skeleton/index';
import { formatDateTimeByTag, formatDayByTag } from '@ja/core-ui/lib/date';

import { useKeyboardShortcuts } from '../composables/useKeyboardShortcuts';
import { useQuickViewerData } from '../composables/useQuickViewerData';
import { useTranslatable } from '../composables/useTranslatable';
import type { ActionConfig, FieldConfig, QuickViewerConfig } from '../types';
import { edit, show } from '../wayfinder/routes';

import DurationValue from './DurationValue.vue';

/**
 * Keys with this suffix are integer-minute durations and render via DurationValue.
 * Convention-driven so displayFields + blocks can stay as plain string arrays.
 */
const DURATION_KEY_SUFFIX = '_duration_minutes';
function isDurationKey(key: string): boolean {
    return key.endsWith(DURATION_KEY_SUFFIX);
}

interface Props {
    model: string;
    id: string | number;
    /** Initial item data (will be replaced by fetched data) */
    item?: Record<string, unknown>;
    config: QuickViewerConfig;
    translationPrefix?: string | null;
    /** Render content only (for embedding in external popover) */
    isContentOnly?: boolean;
}

const props = defineProps<Props>();
const { t, locale } = useI18n();
const { trField, trBlock, trAction, trBadge, trValue } = useTranslatable({
    translationPrefix: props.translationPrefix,
});

const open = ref(false);

// Fetch data when HoverCard opens
const {
    item: fetchedItem,
    isLoading,
    isFetching,
    isFromCache,
    formattedCacheAge,
} = useQuickViewerData({
    model: props.model,
    id: props.id,
    initialItem: props.item,
    enabled: open,
});

// Use fetched item or initial item
const displayItem = computed(() => fetchedItem.value);

// Title and subtitle
const title = computed(() => {
    const field = props.config.titleField ?? 'id';
    return displayItem.value[field] ?? props.id;
});

const subtitle = computed(() => {
    const field = props.config.subtitleField;
    if (!field) return null;
    const raw = displayItem.value[field] ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    return trValue(field, raw as string | number | boolean, String(raw));
});

// Image
const image = computed(() => {
    const field = props.config.imageField;
    if (!field) return null;
    return displayItem.value[field] as string | null;
});

// Header fields (metadata)
const headerFields = computed(() => {
    return props.config.headerFields ?? [];
});

// Blocks (field grouping)
const blocks = computed(() => {
    return props.config.blocks ?? [];
});

// Legacy displayFields support (backward compatibility)
const displayFields = computed(() => {
    return props.config.displayFields ?? [];
});

// Actions
const actions = computed(() => {
    return props.config.actions ?? [];
});

// Has any content to display
const hasBlocks = computed(() => blocks.value.length > 0);
const hasDisplayFields = computed(() => !hasBlocks.value && displayFields.value.length > 0);

// Get field value
function getFieldValue(fieldKey: string): unknown {
    return displayItem.value[fieldKey] ?? null;
}

// Get value from FieldConfig
function getValue(field: FieldConfig): unknown {
    return displayItem.value[field.key] ?? field.default ?? null;
}

// Format value for display (field-type-aware)
function formatValue(field: FieldConfig | string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return t('crud.none');
    }

    // If field is just a string, use basic formatting
    if (typeof field === 'string') {
        if (typeof value === 'boolean') {
            return value ? t('crud.yes') : t('crud.no');
        }
        if (value instanceof Date) {
            return formatDayByTag(value, locale.value);
        }
        if (Array.isArray(value)) {
            return value.join(', ');
        }
        return trValue(field, value as string | number | boolean, String(value));
    }

    // Field-type-aware formatting
    if (field.type === 'boolean' || field.type === 'switch' || field.type === 'checkbox') {
        return value ? t('crud.yes') : t('crud.no');
    }

    if (field.type === 'date' && value) {
        return formatDayByTag(String(value), locale.value);
    }

    if (field.type === 'datetime' && value) {
        return formatDateTimeByTag(String(value), locale.value);
    }

    if (field.type === 'badge') {
        const options = field.options as any[] | undefined;
        const option = options?.find((o) => String(o.value) === String(value));
        return option?.label ?? String(value);
    }

    if (Array.isArray(value)) {
        return value.join(', ');
    }

    return String(value);
}

// Get field label
function getFieldLabel(fieldKey: string): string {
    return trField(fieldKey);
}

// Width classes
const widthClasses = computed(() => {
    switch (props.config.width) {
        case 'sm':
            return 'w-64';
        case 'md':
            return 'w-80';
        case 'lg':
            return 'w-96';
        case 'xl':
            return 'w-[28rem]';
        default:
            return 'w-80';
    }
});

// Get icon component
function getIcon(iconName: string | null | undefined) {
    if (!iconName) return null;
    return (LucideIcons as Record<string, unknown>)[iconName] ?? null;
}

// Handle action execution
const showConfirmDialog = ref(false);
const confirmAction = ref<ActionConfig | null>(null);

function handleAction(action: ActionConfig): void {
    if (action.confirm) {
        confirmAction.value = action;
        showConfirmDialog.value = true;
        return;
    }

    executeAction(action);
}

function executeAction(action: ActionConfig): void {
    if (action.type === 'event' && action.eventName) {
        // Dispatch custom event for domain-specific logic
        window.dispatchEvent(
            new CustomEvent(action.eventName, {
                detail: { id: props.id, model: props.model, payload: action.eventPayload },
            }),
        );
        return;
    }

    if (action.route) {
        const url = action.route.replace(':id', String(props.id));
        const method = (action.method?.toLowerCase() ?? 'post') as 'get' | 'post' | 'put' | 'patch' | 'delete';

        if (method === 'get') {
            router.get(url, {}, { preserveScroll: true });
        } else {
            (router[method] as (url: string, data?: Record<string, unknown>, options?: Record<string, unknown>) => void)(url, {}, {
                preserveScroll: true,
            });
        }
        return;
    }

    // Default actions
    if (action.name === 'view') {
        router.visit(show({ model: props.model, id: String(props.id) }).url);
    } else if (action.name === 'edit') {
        router.visit(edit({ model: props.model, id: String(props.id) }).url);
    }
}

// Keyboard shortcuts
const visibleActions = computed(() => actions.value.filter((a: ActionConfig) => !a.hidden && a.shortcut));

if (!props.isContentOnly) {
    useKeyboardShortcuts(visibleActions, handleAction, {
        enabled: open,
    });
}
</script>

<template>
    <!-- Content-only mode (for embedding in external containers) -->
    <div v-if="isContentOnly" class="space-y-3 p-4">
        <!-- Loading skeleton -->
        <template v-if="isLoading && !displayItem">
            <div class="flex items-center gap-3">
                <Skeleton class="size-10 rounded-full" />
                <div class="flex-1 space-y-2">
                    <Skeleton class="h-4 w-3/4" />
                    <Skeleton class="h-3 w-1/2" />
                </div>
            </div>
            <Skeleton class="h-px w-full" />
            <div class="space-y-2">
                <Skeleton class="h-4 w-full" />
                <Skeleton class="h-4 w-5/6" />
                <Skeleton class="h-4 w-4/6" />
            </div>
        </template>

        <!-- Content -->
        <template v-else>
            <!-- Header -->
            <div class="flex items-start gap-3">
                <Avatar v-if="image || config.imageField" class="size-10 shrink-0">
                    <AvatarImage v-if="image" :src="String(image)" />
                    <AvatarFallback class="text-sm">
                        {{ String(title).charAt(0).toUpperCase() }}
                    </AvatarFallback>
                </Avatar>

                <div class="min-w-0 flex-1">
                    <h4 class="truncate font-semibold leading-tight">{{ title }}</h4>
                    <p v-if="subtitle" class="text-muted-foreground mt-0.5 truncate text-sm">
                        {{ subtitle }}
                    </p>
                </div>

                <!-- Cache indicator -->
                <Badge v-if="isFromCache && formattedCacheAge" variant="outline" class="shrink-0 text-[10px] opacity-60">
                    {{ formattedCacheAge }}
                </Badge>
            </div>

            <!-- Header fields (metadata) -->
            <div v-if="headerFields.length > 0 && config.showMetadata !== false" class="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
                <span v-for="fieldKey in headerFields" :key="fieldKey" class="inline-flex items-center gap-1">
                    <span class="font-medium">{{ getFieldLabel(fieldKey) }}:</span>
                    <span>{{ formatValue(fieldKey, getFieldValue(fieldKey)) }}</span>
                </span>
            </div>

            <Separator v-if="(headerFields.length > 0 && config.showMetadata !== false) || hasBlocks || hasDisplayFields" />

            <!-- Blocks (organized field groups) -->
            <div v-if="hasBlocks" class="space-y-4">
                <div v-for="block in blocks" :key="block.key" class="space-y-2">
                    <!-- Block header -->
                    <div class="flex items-center gap-2">
                        <component v-if="block.icon" :is="getIcon(block.icon)" class="text-muted-foreground size-3.5" />
                        <h5 class="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                            {{ trBlock(block.key) }}
                        </h5>
                    </div>

                    <!-- Block fields -->
                    <dl class="space-y-1.5">
                        <div v-for="field in block.fields" :key="field.key" class="flex items-baseline justify-between gap-2 text-sm">
                            <dt class="text-muted-foreground shrink-0">
                                {{ trField(field.key) }}
                            </dt>
                            <dd class="truncate text-right font-medium">
                                <Badge v-if="field.type === 'badge'" :variant="trBadge(getValue(field) as string, 'default') as any" class="text-xs">
                                    {{ formatValue(field, getValue(field)) }}
                                </Badge>
                                <DurationValue
                                    v-else-if="field.type === 'duration' || isDurationKey(field.key)"
                                    :model-value="getValue(field) as number | null | undefined"
                                    default-mode="hhmm"
                                />
                                <span v-else>{{ formatValue(field, getValue(field)) }}</span>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <!-- Legacy displayFields (backward compatibility) -->
            <dl v-else-if="hasDisplayFields" class="space-y-1.5">
                <div v-for="fieldKey in displayFields" :key="fieldKey" class="flex items-baseline justify-between gap-2 text-sm">
                    <dt class="text-muted-foreground shrink-0">
                        {{ getFieldLabel(fieldKey) }}
                    </dt>
                    <dd class="truncate text-right font-medium">
                        <DurationValue
                            v-if="isDurationKey(fieldKey)"
                            :model-value="getFieldValue(fieldKey) as number | null | undefined"
                            default-mode="hhmm"
                        />
                        <span v-else>{{ formatValue(fieldKey, getFieldValue(fieldKey)) }}</span>
                    </dd>
                </div>
            </dl>

            <!-- Actions -->
            <div v-if="config.showActions !== false && actions.length > 0" class="flex gap-2 pt-1">
                <Button
                    v-for="action in actions.filter((a: ActionConfig) => !a.hidden)"
                    :key="action.name"
                    :variant="(action.color as any) ?? 'outline'"
                    size="sm"
                    class="h-8 flex-1"
                    @click="handleAction(action)"
                >
                    <component v-if="action.icon" :is="getIcon(action.icon)" class="mr-1.5 size-3.5" />
                    {{ trAction(action.name) }}
                </Button>
            </div>
        </template>
    </div>

    <!-- HoverCard mode (default) -->
    <HoverCard v-else v-model:open="open" :open-delay="200" :close-delay="150">
        <HoverCardTrigger as-child>
            <slot>
                <Button variant="ghost" size="sm">
                    <Eye class="mr-2 size-4" />
                    {{ t('crud.quick_view') }}
                </Button>
            </slot>
        </HoverCardTrigger>
        <HoverCardContent :class="widthClasses" align="start">
            <!-- Loading skeleton -->
            <template v-if="isLoading && !displayItem">
                <div class="space-y-3">
                    <div class="flex items-center gap-3">
                        <Skeleton class="size-10 rounded-full" />
                        <div class="flex-1 space-y-2">
                            <Skeleton class="h-4 w-3/4" />
                            <Skeleton class="h-3 w-1/2" />
                        </div>
                    </div>
                    <Skeleton class="h-px w-full" />
                    <div class="space-y-2">
                        <Skeleton class="h-4 w-full" />
                        <Skeleton class="h-4 w-5/6" />
                        <Skeleton class="h-4 w-4/6" />
                    </div>
                </div>
            </template>

            <!-- Content -->
            <template v-else>
                <div class="space-y-3">
                    <!-- Header -->
                    <div class="flex items-start gap-3">
                        <Avatar v-if="image || config.imageField" class="size-10 shrink-0">
                            <AvatarImage v-if="image" :src="String(image)" />
                            <AvatarFallback class="text-sm">
                                {{ String(title).charAt(0).toUpperCase() }}
                            </AvatarFallback>
                        </Avatar>

                        <div class="min-w-0 flex-1">
                            <h4 class="truncate font-semibold leading-tight">{{ title }}</h4>
                            <p v-if="subtitle" class="text-muted-foreground mt-0.5 truncate text-sm">
                                {{ subtitle }}
                            </p>
                        </div>

                        <!-- Refresh / Cache indicator -->
                        <div class="flex shrink-0 items-center gap-1">
                            <Badge v-if="isFromCache && formattedCacheAge" variant="outline" class="text-[10px] opacity-60">
                                {{ formattedCacheAge }}
                            </Badge>
                            <Button v-if="isFetching" variant="ghost" size="icon" class="size-6" disabled>
                                <RefreshCw class="size-3 animate-spin" />
                            </Button>
                        </div>
                    </div>

                    <!-- Header fields (metadata) -->
                    <div
                        v-if="headerFields.length > 0 && config.showMetadata !== false"
                        class="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs"
                    >
                        <span v-for="fieldKey in headerFields" :key="fieldKey" class="inline-flex items-center gap-1">
                            <span class="font-medium">{{ getFieldLabel(fieldKey) }}:</span>
                            <span>{{ formatValue(fieldKey, getFieldValue(fieldKey)) }}</span>
                        </span>
                    </div>

                    <Separator v-if="(headerFields.length > 0 && config.showMetadata !== false) || hasBlocks || hasDisplayFields" />

                    <!-- Blocks (organized field groups) -->
                    <div v-if="hasBlocks" class="space-y-4">
                        <div v-for="block in blocks" :key="block.key" class="space-y-2">
                            <!-- Block header -->
                            <div class="flex items-center gap-2">
                                <component v-if="block.icon" :is="getIcon(block.icon)" class="text-muted-foreground size-3.5" />
                                <h5 class="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                                    {{ trBlock(block.key) }}
                                </h5>
                            </div>

                            <!-- Block fields -->
                            <dl class="space-y-1.5">
                                <div v-for="field in block.fields" :key="field.key" class="flex items-baseline justify-between gap-2 text-sm">
                                    <dt class="text-muted-foreground shrink-0">
                                        {{ trField(field.key) }}
                                    </dt>
                                    <dd class="truncate text-right font-medium">
                                        <Badge
                                            v-if="field.type === 'badge'"
                                            :variant="trBadge(getValue(field) as string, 'default') as any"
                                            class="text-xs"
                                        >
                                            {{ formatValue(field, getValue(field)) }}
                                        </Badge>
                                        <DurationValue
                                            v-else-if="field.type === 'duration' || isDurationKey(field.key)"
                                            :model-value="getValue(field) as number | null | undefined"
                                            default-mode="hhmm"
                                        />
                                        <span v-else>{{ formatValue(field, getValue(field)) }}</span>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <!-- Legacy displayFields (backward compatibility) -->
                    <dl v-else-if="hasDisplayFields" class="space-y-1.5">
                        <div v-for="fieldKey in displayFields" :key="fieldKey" class="flex items-baseline justify-between gap-2 text-sm">
                            <dt class="text-muted-foreground shrink-0">
                                {{ getFieldLabel(fieldKey) }}
                            </dt>
                            <dd class="truncate text-right font-medium">
                                <DurationValue
                                    v-if="isDurationKey(fieldKey)"
                                    :model-value="getFieldValue(fieldKey) as number | null | undefined"
                                    default-mode="hhmm"
                                />
                                <span v-else>{{ formatValue(fieldKey, getFieldValue(fieldKey)) }}</span>
                            </dd>
                        </div>
                    </dl>

                    <!-- Actions -->
                    <div v-if="config.showActions !== false && actions.length > 0" class="flex gap-2 pt-1">
                        <Button
                            v-for="action in actions.filter((a: ActionConfig) => !a.hidden)"
                            :key="action.name"
                            :variant="(action.color as any) ?? 'outline'"
                            size="sm"
                            class="h-8 flex-1"
                            @click="handleAction(action)"
                        >
                            <component v-if="action.icon" :is="getIcon(action.icon)" class="mr-1.5 size-3.5" />
                            {{ trAction(action.name) }}
                        </Button>
                    </div>
                </div>
            </template>
        </HoverCardContent>
    </HoverCard>

    <!-- Confirmation Dialog -->
    <AlertDialog v-model:open="showConfirmDialog">
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>
                    {{
                        confirmAction?.confirmTitle ??
                        t('crud.confirm_action', {
                            action: trAction(confirmAction?.name ?? ''),
                        })
                    }}
                </AlertDialogTitle>
                <AlertDialogDescription>
                    {{ confirmAction?.confirmMessage ?? t('crud.confirm_action_message') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('crud.cancel') }}</AlertDialogCancel>
                <AlertDialogAction @click="confirmAction && executeAction(confirmAction)">
                    {{ t('crud.confirm') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
